import styled from "@emotion/styled";
import { useCallback, useEffect, useRef, useState } from "react";

import { WizardContainer } from "@/components/onboarding/components/welcome-wizard";
import {
  MDSBadge,
  MDSBadgeVariant,
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import {
  MeetingFollowThroughAnimation,
  type MeetingTrialAnimationMode,
} from "./meeting-follow-through-animation";
import {
  DEFAULT_MEETING_TRIAL_PITCH_TIMINGS,
  type MeetingTrialPitchTimings,
} from "./meeting-trial-pitch-timings";
import { ProactivePlanSheet, type ProactivePlanSheetEntrance } from "./proactive-plan-sheet";

export type MeetingTrialPlanSheetEntrance = ProactivePlanSheetEntrance;

interface FocusRestoreTarget {
  focus: () => void;
}

export interface MeetingTrialPitchProps {
  animationMode?: MeetingTrialAnimationMode;
  initialPlanSheetEntrance?: MeetingTrialPlanSheetEntrance;
  isCheckoutLoading?: boolean;
  showReplay?: boolean;
  timings?: MeetingTrialPitchTimings;
  onContinueFree: () => void;
  onContinueToCheckout: () => void;
}

/**
 * Isolated meeting-focused Proactive pitch used for visual iteration.
 *
 * The component owns its animation and plan-sheet interaction, while callers
 * retain the consequential choices: continuing with Free and starting Stripe
 * checkout. Dismissing the sheet never invokes either callback.
 */
export const MeetingTrialPitch = ({
  animationMode = "play",
  initialPlanSheetEntrance,
  isCheckoutLoading = false,
  showReplay = true,
  timings = DEFAULT_MEETING_TRIAL_PITCH_TIMINGS,
  onContinueFree,
  onContinueToCheckout,
}: MeetingTrialPitchProps) => {
  const [planSheetEntrance, setPlanSheetEntrance] = useState<MeetingTrialPlanSheetEntrance | null>(
    initialPlanSheetEntrance ?? null
  );
  const [planSheetIsVisible, setPlanSheetIsVisible] = useState(false);
  const sheetCloseTimerRef = useRef<number>();
  const focusRestoreFrameRef = useRef<number>();
  const planSheetTriggerRef = useRef<FocusRestoreTarget | null>(null);
  const trialButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (planSheetEntrance === null) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => setPlanSheetIsVisible(true));
    return () => window.cancelAnimationFrame(animationFrame);
  }, [planSheetEntrance]);

  useEffect(() => {
    return () => {
      if (sheetCloseTimerRef.current !== undefined) {
        window.clearTimeout(sheetCloseTimerRef.current);
      }

      if (focusRestoreFrameRef.current !== undefined) {
        window.cancelAnimationFrame(focusRestoreFrameRef.current);
      }
    };
  }, []);

  const openPlanSheet = useCallback(
    (entrance: MeetingTrialPlanSheetEntrance, trigger: FocusRestoreTarget | null) => {
      if (sheetCloseTimerRef.current !== undefined) {
        window.clearTimeout(sheetCloseTimerRef.current);
        sheetCloseTimerRef.current = undefined;
      }

      planSheetTriggerRef.current = trigger;
      setPlanSheetIsVisible(false);
      setPlanSheetEntrance(entrance);
    },
    []
  );

  const closePlanSheet = useCallback(() => {
    setPlanSheetIsVisible(false);

    const closeDurationMs = Math.max(timings.sheetScrimMs, timings.sheetSlideMs);
    sheetCloseTimerRef.current = window.setTimeout(() => {
      const trigger = planSheetTriggerRef.current;

      setPlanSheetEntrance(null);
      sheetCloseTimerRef.current = undefined;
      focusRestoreFrameRef.current = window.requestAnimationFrame(() => {
        trigger?.focus();
        focusRestoreFrameRef.current = undefined;
      });
    }, closeDurationMs);
  }, [timings.sheetScrimMs, timings.sheetSlideMs]);

  return (
    <PitchViewport data-layout="wide">
      <PitchScreen>
        <CopyColumn>
          <div>
            <OfferBadge label="Mem Proactive" variant={MDSBadgeVariant.Emphasis} />
            <Headline variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
              Meeting notes remember. Mem Agent follows through.
            </Headline>
            <BodyCopy variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Secondary}>
              After a meeting, Mem Agent can find your next steps, turn them into a useful plan, and
              bring back anything that&apos;s still unfinished.
            </BodyCopy>
          </div>

          <ActionBlock>
            <PrimaryButton
              ref={trialButtonRef}
              size={MDSButtonSize.Large}
              variant={MDSButtonVariant.Emphasis}
              onPress={() => openPlanSheet("trial", trialButtonRef.current)}
            >
              <PrimaryCtaCopy>
                <PrimaryCtaLabel>Let Mem keep track for me</PrimaryCtaLabel>
                <PrimaryCtaSubline>
                  Start your 7-day free trial of Mem Proactive
                </PrimaryCtaSubline>
              </PrimaryCtaCopy>
            </PrimaryButton>
            <FreeButton
              size={MDSButtonSize.Large}
              variant={MDSButtonVariant.Transparent}
              onPress={onContinueFree}
            >
              Continue without follow-ups
            </FreeButton>
            <FinePrint variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
              Renews at $99/month · 30-day money-back guarantee
            </FinePrint>
          </ActionBlock>
        </CopyColumn>

        <MeetingFollowThroughAnimation
          mode={animationMode}
          showReplay={showReplay}
          timings={timings}
        />
      </PitchScreen>

      {planSheetEntrance !== null ? (
        <ProactivePlanSheet
          entrance={planSheetEntrance}
          isCheckoutLoading={isCheckoutLoading}
          isVisible={planSheetIsVisible}
          timings={timings}
          onClose={closePlanSheet}
          onContinueToCheckout={onContinueToCheckout}
        />
      ) : null}
    </PitchViewport>
  );
};

const PitchViewport = styled(WizardContainer)`
  position: relative;

  &[data-layout="wide"] {
    height: 480px;
  }

  @media (max-width: 760px) {
    &[data-layout="wide"] {
      width: 660px;
      max-width: 90vw;
      height: min(820px, calc(94dvh - 24px));
      padding: ${({ theme }) => theme.spacing[5]};
    }
  }

  @media (max-width: 600px) {
    &[data-layout="wide"] {
      width: calc(100vw - 24px);
      max-width: 440px;
    }
  }
`;

const PitchScreen = styled.div`
  display: grid;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: calc(${({ theme }) => theme.spacing[7]} + ${({ theme }) => theme.spacing[6]});

  @media (max-width: 760px) {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    overflow-x: hidden;
    overflow-y: auto;
    padding-bottom: 176px;

    > [data-animation-column] {
      width: 100%;
      flex: none;
      order: 1;
    }
  }
`;

const CopyColumn = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;

  @media (max-width: 760px) {
    display: contents;

    > div:first-of-type {
      order: 0;
    }
  }
`;

const Headline = styled(MDSText)`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  color: ${({ theme }) => theme.colors.text.contrast};
  line-height: 1.35;
`;

const OfferBadge = styled(MDSBadge)`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const BodyCopy = styled(MDSText)`
  display: block;
  max-width: 430px;
  line-height: 1.6;
`;

const ActionBlock = styled.div`
  margin-top: auto;
  margin-bottom: calc(${({ theme }) => theme.spacing[7]} + ${({ theme }) => theme.spacing[4]});

  @media (max-width: 760px) {
    position: absolute;
    z-index: 2;
    right: ${({ theme }) => theme.spacing[5]};
    bottom: ${({ theme }) => theme.spacing[5]};
    left: ${({ theme }) => theme.spacing[5]};
    min-width: 0;
    margin: 0;
    padding-top: ${({ theme }) => theme.spacing[2]};
    background: ${({ theme }) => theme.colors.surface.modal};
  }
`;

const PrimaryButton = styled(MDSButton)`
  width: 100%;
  min-width: 0;
  min-height: 56px;
`;

const PrimaryCtaCopy = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
`;

const PrimaryCtaLabel = styled.span`
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
`;

const PrimaryCtaSubline = styled.span`
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
  opacity: 0.82;
`;

const FreeButton = styled(MDSButton)`
  width: 100%;
  min-width: 0;
  min-height: 44px;
  margin-top: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FinePrint = styled(MDSText)`
  display: block;
  margin-top: ${({ theme }) => theme.spacing[2]};
  text-align: center;
  line-height: 1.55;
  white-space: nowrap;

  @media (max-width: 760px) {
    font-size: clamp(8px, 2.2vw, 10px);
  }
`;
